import React, { useCallback, useEffect, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import NavigationBar from '@components/NavigationBar/NavigationBar';
import { SidebarExpandHandle } from '@components/SidebarRail';
import useSnackbar from '@hooks/useSnackbar';
import { useSidebar } from '@contexts/useSidebar';
import type { CreateProcessRequest } from '@api/processes';
import NewProcessDialog from '../components/NewProcessDialog';
import { useProcessActions } from '../hooks/useProcessActions';
import { useProcessDetail } from '../hooks/useProcessDetail';
import NodeLibraryFlyout from './sidebar/NodeLibraryFlyout';
import ProcessEditorRail from './sidebar/ProcessEditorRail';
import ProcessesThemeBoundary from '../ProcessesThemeBoundary';

/**
 * Sidebar slot for the authenticated app shell.
 *
 * On non-editor routes we mount the global {@code NavigationBar} (the wide
 * 292px drawer with workspace switcher + recents + settings).
 *
 * Inside the process editor (`/processes/:processId`) we swap to one of
 * two presentations driven by `isEditorRailExpanded` on SidebarContext:
 *
 *   - Collapsed (default): the icon rail + slide-in NodeLibrary flyout,
 *     mirroring the prototype's `.nav--collapsed`.
 *
 *   - Expanded: the same wide `NavigationBar` used on every other route,
 *     whose collapse button flips the flag back to false.
 *
 * The OS workspace switcher on the rail expands AND auto-pops the
 * `ProductSwitcher` dropdown by calling `openProductSwitcher()` on
 * `SidebarContext` BEFORE expanding the rail. The switcher's open
 * state lives on the context (not as local state inside the
 * component) precisely so this handshake survives the
 * mount/unmount cycle: the rail unmounts NavigationBar (and the
 * switcher) when collapsed, and the freshly-mounted switcher reads its
 * open state straight from context on its first render.
 *
 * The user can also publish a process; once published the editor goes
 * read-only. The rail honours that by disabling every action button on
 * it EXCEPT the OS workspace switcher and the expand button (those are
 * navigations, not graph mutations) — the design's escape hatch so the
 * user is never trapped in a locked editor.
 */
const ProcessEditorSidebar: React.FC = () => {
  const navigate = useNavigate();
  const editorMatch = useMatch('/processes/:processId');
  const runDetailMatch = useMatch('/processes/:processId/runs/:runId');
  const activeMatch = runDetailMatch ?? editorMatch;
  const processId = activeMatch?.params.processId;
  const { showSuccess, showError } = useSnackbar();
  const processActions = useProcessActions();

  const {
    isEditorRailExpanded,
    setEditorRailExpanded,
    openProductSwitcher,
  } = useSidebar();

  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Surface the published flag so the rail's action buttons can be
  // disabled. Disabled because we only need this in the editor; outside
  // the editor `processId` is undefined and the hook short-circuits.
  const detailQuery = useProcessDetail(processId);
  const isPublished = detailQuery.data?.status === 'published';

  // When the user navigates AWAY from the editor route, snap the rail
  // back to its collapsed default. Otherwise a subsequent visit would
  // briefly render the wide NavigationBar before any of the editor's
  // own state mounts, which flashes the canvas at the wrong width.
  useEffect(() => {
    if (!activeMatch && isEditorRailExpanded) {
      setEditorRailExpanded(false);
    }
  }, [activeMatch, isEditorRailExpanded, setEditorRailExpanded]);

  // Effective open state — derived, NOT mirrored into local state.
  // Publishing locks the canvas read-only and we already disable the
  // rail's Search button via `disabled={isPublished}` so the user
  // can't re-open the flyout — but if it was already open at publish
  // time we don't want it to stay anchored on screen looking usable
  // while in fact every drag-source row is a no-op against a
  // read-only canvas. Computing the rendered open state at render
  // time (instead of an effect that flips local state) avoids the
  // setState-in-effect cascade lint rule and keeps the local
  // `flyoutOpen` flag honest if the user later un-publishes.
  const effectiveFlyoutOpen = flyoutOpen && !isPublished;

  const onToggleFlyout = useCallback(() => {
    setFlyoutOpen((open) => !open);
  }, []);

  const onCloseFlyout = useCallback(() => setFlyoutOpen(false), []);

  const onOpenNewProcessDialog = useCallback(() => setDialogOpen(true), []);
  const onCloseNewProcessDialog = useCallback(() => setDialogOpen(false), []);

  const onExpand = useCallback(() => {
    setFlyoutOpen(false);
    setEditorRailExpanded(true);
  }, [setEditorRailExpanded]);

  const onSwitchWorkspace = useCallback(() => {
    setFlyoutOpen(false);
    // ORDER MATTERS — open the switcher BEFORE expanding the rail. The
    // rail-expand will fresh-mount NavigationBar (and ProductSwitcher
    // inside it); the switcher then reads its open state straight from
    // context on its first render, and the dropdown shows up in the
    // same paint as the expanded sidebar. Flipping these two would
    // queue two separate renders, but functionally the result is the
    // same — context is shared so both writes commit before the next
    // paint either way.
    openProductSwitcher();
    setEditorRailExpanded(true);
  }, [openProductSwitcher, setEditorRailExpanded]);

  const onCollapse = useCallback(() => {
    setEditorRailExpanded(false);
  }, [setEditorRailExpanded]);

  const onCreateProcess = useCallback(
    async (request: CreateProcessRequest) => {
      try {
        const created = await processActions.create(request);
        setDialogOpen(false);
        showSuccess(`Created “${created.name}”.`);
        navigate(`/processes/${encodeURIComponent(created.processId)}`);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to create process.';
        showError(message);
        throw error;
      }
    },
    [navigate, processActions, showError, showSuccess],
  );

  if (!activeMatch) {
    return <NavigationBar />;
  }

  if (isEditorRailExpanded) {
    return (
      <ProcessesThemeBoundary>
        <NavigationBar onCollapse={onCollapse} />
        <NewProcessDialog
          open={dialogOpen}
          onClose={onCloseNewProcessDialog}
          onCreate={onCreateProcess}
          isCreating={processActions.isCreating}
          dataTestId="process-editor-sidebar__new-process-dialog"
        />
      </ProcessesThemeBoundary>
    );
  }

  return (
    <ProcessesThemeBoundary>
      <ProcessEditorRail
        flyoutOpen={effectiveFlyoutOpen}
        onToggleFlyout={onToggleFlyout}
        onOpenNewProcessDialog={onOpenNewProcessDialog}
        onSwitchWorkspace={onSwitchWorkspace}
        disabled={isPublished}
      />
      <SidebarExpandHandle onExpand={onExpand} dataTestId="process-editor-sidebar__expand" />
      <NodeLibraryFlyout open={effectiveFlyoutOpen} onClose={onCloseFlyout} />
      <NewProcessDialog
        open={dialogOpen}
        onClose={onCloseNewProcessDialog}
        onCreate={onCreateProcess}
        isCreating={processActions.isCreating}
        dataTestId="process-editor-sidebar__new-process-dialog"
      />
    </ProcessesThemeBoundary>
  );
};

export default ProcessEditorSidebar;
